import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  listarPdvs,
  listarClientesZenMobile,
  listarClientesZenTag,
  listarClientesZenGestao,
  obterRevendaPorId,
} from "../services/api";
import { ArrowLeft, CheckCircle, XCircle } from "lucide-react";

const takeItems = (resp) => {
  const d = resp?.data;

  // data já é array
  if (Array.isArray(d)) return d;

  // data.items ou data.Items
  const a = d?.items ?? d?.Items ?? [];
  return Array.isArray(a) ? a : [];
};

const isThisMonth = (dateStrOrDate) => {
  if (!dateStrOrDate) return false;
  const d = new Date(dateStrOrDate);
  if (Number.isNaN(d.getTime())) return false;

  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

const Badge = ({ children, kind = "gray" }) => {
  const map = {
    gray: "bg-gray-100 text-gray-700 border-gray-200 dark:bg-white/5 dark:text-slate-200 dark:border-white/10",
    green: "bg-green-50 text-green-700 border-green-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:border-emerald-500/20",
    amber: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-500/10 dark:text-amber-200 dark:border-amber-500/20",
    red: "bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-200 dark:border-red-500/20",
    blue: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-200 dark:border-blue-500/20",
  };
  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border ${map[kind]}`}>
      {children}
    </span>
  );
};

const StatusIcon = ({ ok }) =>
  ok ? (
    <CheckCircle className="inline text-green-600 dark:text-emerald-300" size={18} />
  ) : (
    <XCircle className="inline text-red-600 dark:text-red-300" size={18} />
  );

const renderDateBR = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("pt-BR");
};

const TableShell = ({ children }) => (
  <div className="overflow-x-auto">
    <div className="zen-card rounded-2xl border border-gray-200 dark:bg-white/5 dark:border-white/10">
      {children}
    </div>
  </div>
);

const RevendaLicencas = ({ token }) => {
  const navigate = useNavigate();
  const { revendaId } = useParams();

  const [loading, setLoading] = useState(true);
  const [revendaNome, setRevendaNome] = useState("");

  const [pdvs, setPdvs] = useState([]);
  const [zenmobile, setZenmobile] = useState([]);
  const [zentag, setZentag] = useState([]);
  const [zengestao, setZengestao] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // nome da revenda
        try {
          const rr = await obterRevendaPorId(revendaId, token);
          const r = rr?.data;
          setRevendaNome(r?.nomeFantasia || r?.razaoSocial || "");
        } catch {
          setRevendaNome("");
        }

        // garante número quando o backend espera number
        const revIdNum = Number(revendaId);

        const [rPdv, rMob, rTag, rGes] = await Promise.all([
          listarPdvs(token, { revendaId: revIdNum }),
          listarClientesZenMobile(token, { revendaId: revIdNum, page: 1, pageSize: 200, ativo: undefined }),
          listarClientesZenTag(token, { revendaId: revIdNum, page: 1, pageSize: 200, ativo: undefined }),
          listarClientesZenGestao(token, { revendaId: revIdNum, page: 1, pageSize: 200, ativo: undefined }),
        ]);

        setPdvs(Array.isArray(rPdv?.data) ? rPdv.data : []);
        setZenmobile(takeItems(rMob));
        setZentag(takeItems(rTag));
        setZengestao(takeItems(rGes));
      } catch (e) {
        console.error(e);
        setPdvs([]);
        setZenmobile([]);
        setZentag([]);
        setZengestao([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token, revendaId]);

  const computePdvBloqueado = (pdv) => {
    if (pdv?.bloqueado !== undefined && pdv?.bloqueado !== null) return !!pdv.bloqueado;
    const vencida = !pdv?.validadeChave || new Date(pdv.validadeChave).getTime() < Date.now();
    return !pdv?.ativo || vencida;
  };

  const stats = useMemo(() => {
    const pdvNew = pdvs.filter((x) => isThisMonth(x.criadoEm || x.ativadoEm)).length;
    const mobNew = zenmobile.filter((x) => isThisMonth(x.criadoEm || x.ativadoEm)).length;
    const tagNew = zentag.filter((x) => isThisMonth(x.criadoEm || x.ativadoEm)).length;
    const gesNew = zengestao.filter((x) => isThisMonth(x.criadoEm || x.ativadoEm)).length;

    return {
      pdv: { total: pdvs.length, novos: pdvNew },
      mob: { total: zenmobile.length, novos: mobNew },
      tag: { total: zentag.length, novos: tagNew },
      ges: { total: zengestao.length, novos: gesNew },
      novosTotal: pdvNew + mobNew + tagNew + gesNew,
    };
  }, [pdvs, zenmobile, zentag, zengestao]);

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-600 dark:text-slate-200">
        Carregando licenças da revenda...
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        {/* topo */}
        <div className="mb-6">
          <button
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-slate-300 dark:hover:text-white"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} /> Voltar
          </button>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
            Licenças da Revenda #{revendaId}
            {revendaNome ? <span className="text-blue-700 dark:text-blue-300">{" "} - {revendaNome}</span> : null}
          </h2>

          <p className="text-sm text-gray-500 dark:text-slate-300/80 mt-1">
            Destaque: <b>Novos este mês</b> (carência 1 mês). Total novos: <b>{stats.novosTotal}</b>
          </p>
        </div>

        {/* cards resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          {[
            { title: "ZenPDV", total: stats.pdv.total, novos: stats.pdv.novos },
            { title: "ZenMobile", total: stats.mob.total, novos: stats.mob.novos },
            { title: "ZenTag", total: stats.tag.total, novos: stats.tag.novos },
            { title: "ZenGestão", total: stats.ges.total, novos: stats.ges.novos },
          ].map((c) => (
            <div
              key={c.title}
              className="zen-card rounded-2xl p-4 border border-gray-200 dark:bg-white/5 dark:border-white/10"
            >
              <div className="font-semibold text-gray-900 dark:text-white">{c.title}</div>
              <div className="text-sm text-gray-600 dark:text-slate-300 mt-1">{c.total} licenças</div>
              <div className="mt-2">
                <Badge kind={c.novos ? "amber" : "gray"}>Novos este mês: {c.novos}</Badge>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-8">
          {/* ZenPDV */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">ZenPDV</h3>
              <button
                className="text-sm text-blue-700 hover:underline dark:text-blue-300"
                onClick={() => navigate(`/licenciamento/zenpdv?revendaId=${revendaId}`)}
              >
                Abrir lista completa
              </button>
            </div>

            <TableShell>
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-white/5">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Cliente</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Descrição</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Cód. Instalação</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Vencimento</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Ativação</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Status</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Carência</th>
                  </tr>
                </thead>

                <tbody className="text-gray-700 dark:text-slate-200">
                  {pdvs.slice(0, 15).map((x) => {
                    const novo = isThisMonth(x.criadoEm || x.ativadoEm);
                    const bloqueado = computePdvBloqueado(x);
                    const ativoOk = !bloqueado;

                    return (
                      <tr
                        key={x.id}
                        className="border-t border-gray-200 hover:bg-gray-50 transition dark:border-white/10 dark:hover:bg-white/5"
                      >
                        <td className="py-3 px-4">{x.clienteNome || "-"}</td>
                        <td className="py-3 px-4">{x.descricao || "-"}</td>
                        <td className="py-3 px-4">{x.codigoInstalacao || "-"}</td>
                        <td className="py-3 px-4">{renderDateBR(x.validadeChave)}</td>
                        <td className="py-3 px-4">{renderDateBR(x.criadoEm || x.ativadoEm)}</td>
                        <td className="py-3 px-4 text-center">
                          <StatusIcon ok={ativoOk} />
                        </td>
                        <td className="py-3 px-4 text-center">
                          {novo ? <Badge kind="amber">NOVO</Badge> : <span className="text-gray-400 dark:text-slate-400">-</span>}
                        </td>
                      </tr>
                    );
                  })}

                  {pdvs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-500 dark:text-slate-300/80">
                        Nenhuma licença ZenPDV.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </TableShell>

            {pdvs.length > 15 && (
              <div className="text-xs text-gray-500 dark:text-slate-300/70 mt-2">
                Mostrando 15 de {pdvs.length}.
              </div>
            )}
          </div>

          {/* ZenMobile */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">ZenMobile</h3>
              <button
                className="text-sm text-blue-700 hover:underline dark:text-blue-300"
                onClick={() => navigate(`/licenciamento/zenmobile/list?revendaId=${revendaId}`)}
              >
                Abrir lista completa
              </button>
            </div>

            <TableShell>
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-white/5">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Cliente</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Descrição</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Cód. Instalação</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Ativação</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Status</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Carência</th>
                  </tr>
                </thead>

                <tbody className="text-gray-700 dark:text-slate-200">
                  {zenmobile.slice(0, 15).map((x) => {
                    const novo = isThisMonth(x.criadoEm || x.ativadoEm);
                    return (
                      <tr
                        key={x.id}
                        className="border-t border-gray-200 hover:bg-gray-50 transition dark:border-white/10 dark:hover:bg-white/5"
                      >
                        <td className="py-3 px-4">{x.nomeFantasia || x.clienteNome || "-"}</td>
                        <td className="py-3 px-4">{x.descricao || "-"}</td>
                        <td className="py-3 px-4">{x.codigoInstalacao || "-"}</td>
                        <td className="py-3 px-4">{renderDateBR(x.criadoEm || x.ativadoEm)}</td>
                        <td className="py-3 px-4 text-center">
                          <StatusIcon ok={!!x.ativo} />
                        </td>
                        <td className="py-3 px-4 text-center">
                          {novo ? <Badge kind="amber">NOVO</Badge> : <span className="text-gray-400 dark:text-slate-400">-</span>}
                        </td>
                      </tr>
                    );
                  })}

                  {zenmobile.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-gray-500 dark:text-slate-300/80">
                        Nenhuma licença ZenMobile.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </TableShell>

            {zenmobile.length > 15 && (
              <div className="text-xs text-gray-500 dark:text-slate-300/70 mt-2">
                Mostrando 15 de {zenmobile.length}.
              </div>
            )}
          </div>

          {/* ZenTag */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">ZenTag</h3>
              <button
                className="text-sm text-blue-700 hover:underline dark:text-blue-300"
                onClick={() => navigate(`/licenciamento/zentag/list?revendaId=${revendaId}`)}
              >
                Abrir lista completa
              </button>
            </div>

            <TableShell>
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-white/5">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Cliente</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">CNPJ</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Slug</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Ativação</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Status</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Carência</th>
                  </tr>
                </thead>

                <tbody className="text-gray-700 dark:text-slate-200">
                  {zentag.slice(0, 15).map((x) => {
                    const novo = isThisMonth(x.criadoEm || x.ativadoEm);
                    return (
                      <tr
                        key={x.id}
                        className="border-t border-gray-200 hover:bg-gray-50 transition dark:border-white/10 dark:hover:bg-white/5"
                      >
                        <td className="py-3 px-4">{x.nomeFantasia || x.clienteNome || "-"}</td>
                        <td className="py-3 px-4">{x.clienteCnpj || x.cnpj || "-"}</td>
                        <td className="py-3 px-4">{x.slug || "-"}</td>
                        <td className="py-3 px-4">{renderDateBR(x.criadoEm || x.ativadoEm)}</td>
                        <td className="py-3 px-4 text-center">
                          <StatusIcon ok={!!x.ativo} />
                        </td>
                        <td className="py-3 px-4 text-center">
                          {novo ? <Badge kind="amber">NOVO</Badge> : <span className="text-gray-400 dark:text-slate-400">-</span>}
                        </td>
                      </tr>
                    );
                  })}

                  {zentag.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-gray-500 dark:text-slate-300/80">
                        Nenhuma licença ZenTag.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </TableShell>

            {zentag.length > 15 && (
              <div className="text-xs text-gray-500 dark:text-slate-300/70 mt-2">
                Mostrando 15 de {zentag.length}.
              </div>
            )}
          </div>

          {/* ZenGestão */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">ZenGestão</h3>
              <button
                className="text-sm text-blue-700 hover:underline dark:text-blue-300"
                onClick={() => navigate(`/licenciamento/zengestao/list?revendaId=${revendaId}`)}
              >
                Abrir lista completa
              </button>
            </div>

            <TableShell>
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-white/5">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Cliente</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">CNPJ</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Slug</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Ativação</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Status</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Carência</th>
                  </tr>
                </thead>

                <tbody className="text-gray-700 dark:text-slate-200">
                  {zengestao.slice(0, 15).map((x) => {
                    const novo = isThisMonth(x.criadoEm || x.ativadoEm);
                    return (
                      <tr
                        key={x.id}
                        className="border-t border-gray-200 hover:bg-gray-50 transition dark:border-white/10 dark:hover:bg-white/5"
                      >
                        <td className="py-3 px-4">{x.nomeFantasia || x.clienteNome || "-"}</td>
                        <td className="py-3 px-4">{x.clienteCnpj || x.cnpj || "-"}</td>
                        <td className="py-3 px-4">{x.slug || "-"}</td>
                        <td className="py-3 px-4">{renderDateBR(x.criadoEm || x.ativadoEm)}</td>
                        <td className="py-3 px-4 text-center">
                          <StatusIcon ok={!!x.ativo} />
                        </td>
                        <td className="py-3 px-4 text-center">
                          {novo ? <Badge kind="amber">NOVO</Badge> : <span className="text-gray-400 dark:text-slate-400">-</span>}
                        </td>
                      </tr>
                    );
                  })}

                  {zengestao.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-gray-500 dark:text-slate-300/80">
                        Nenhuma licença ZenGestão.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </TableShell>

            {zengestao.length > 15 && (
              <div className="text-xs text-gray-500 dark:text-slate-300/70 mt-2">
                Mostrando 15 de {zengestao.length}.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevendaLicencas;
